import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { cities } from '../../config/cities';
import { services } from '../../config/services';

const ServiceCityPage = () => {
  const { serviceSlug, citySlug } = useParams<{ serviceSlug: string; citySlug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const city = cities.find(c => c.slug === citySlug);
  const service = services.find(s => s.slug === serviceSlug);

  useEffect(() => {
    if (service && city) {
      document.title = `${service.name} in ${city.name} | TaskBudy - Professional ${service.name} Services`;
      
      // Update meta tags
      const updateMetaTag = (name: string, content: string) => {
        let meta = document.querySelector(`meta[name="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      updateMetaTag('description', `Professional ${service.name} services in ${city.name}. Trusted, verified tradespeople available 24/7. Instant booking, transparent pricing, and quality guaranteed.`);
      updateMetaTag('keywords', `${service.name}, ${city.name}, ${service.name} ${city.name}, professional ${service.name}, emergency ${service.name}`);
      updateMetaTag('geo.region', 'GB');
      updateMetaTag('geo.placename', city.name);
    }
  }, [service, city]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!city || !service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/services" className="text-teal-600 hover:text-teal-700">
            View All Services
          </Link>
        </div>
      </div>
    );
  }

  const handleBookNow = () => {
    setLoading(true);
    navigate('/service-request', { 
      state: { 
        selectedService: service.name,
        selectedCity: city.name 
      } 
    });
  };

  const otherServices = services.filter(s => s.slug !== serviceSlug);
  const nearbyCities = cities.filter(c => c.slug !== citySlug).slice(0, 6);

  return (
    <>
      {/* Navigation */}
      <nav className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-50 via-white to-orange-50 pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-teal-100 rounded-full mb-6">
                <i className={`${service.icon} text-4xl text-teal-600`}></i>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Professional {service.name} Services in {city.name}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {service.description} - Verified professionals, instant booking, and competitive rates in {city.name}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleBookNow}
                  disabled={loading}
                  className="px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : 'Book Now'}
                </button>
                <Link
                  to="/pricing"
                  className="px-8 py-4 bg-white text-teal-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors border-2 border-teal-600 whitespace-nowrap"
                >
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Why Choose TaskBudy for {service.name} in {city.name}?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-fill text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Verified Professionals</h3>
                <p className="text-gray-600">
                  All our {service.name.toLowerCase()} professionals in {city.name} are background-checked, insured, and highly rated by customers.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-fill text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Same-Day Service</h3>
                <p className="text-gray-600">
                  Need urgent {service.name.toLowerCase()} help in {city.name}? We offer same-day and emergency services for your convenience.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-pound-circle-fill text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Transparent Pricing</h3>
                <p className="text-gray-600">
                  Know exactly what you'll pay before booking. No hidden fees, no surprises - just honest {service.name.toLowerCase()} pricing in {city.name}.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Service Details */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Expert {service.name} Services in {city.name}
                </h2>
                <p className="text-gray-600 mb-6">
                  Looking for reliable {service.name.toLowerCase()} services in {city.name}? TaskBudy connects you with experienced, vetted professionals who deliver quality work at competitive prices. Whether you need routine maintenance or emergency assistance, our {city.name}-based professionals are ready to help.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Fast Response Times:</strong>
                      <p className="text-gray-600">Most {service.name.toLowerCase()} jobs in {city.name} can be scheduled within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Quality Guaranteed:</strong>
                      <p className="text-gray-600">All work is backed by our satisfaction guarantee and professional insurance</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Local Experts:</strong>
                      <p className="text-gray-600">Our professionals know {city.name} and understand local building codes and requirements</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src={`https://public.readdy.ai/ai/img_res/1339fa27c565905bcbde961b967422d2.jpg$%7Bservice.name.toLowerCase%28%29%7D%20worker%20providing%20quality%20service%20in%20modern%20home%20with%20tools%20and%20equipment%20clean%20bright%20environment%20customer%20satisfaction%20focus&width=600&height=400&seq=${serviceSlug}-${citySlug}-main&orientation=landscape`}
                  alt={`Professional ${service.name} in ${city.name}`}
                  className="rounded-lg shadow-xl w-full h-96 object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              How to Book {service.name} in {city.name}
            </h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Describe Your Job</h3>
                <p className="text-gray-600 text-sm">
                  Tell us what {service.name.toLowerCase()} work you need done in {city.name}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Get Matched</h3>
                <p className="text-gray-600 text-sm">
                  We connect you with qualified {service.name.toLowerCase()} professionals in {city.name}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Schedule Service</h3>
                <p className="text-gray-600 text-sm">
                  Choose a time that works for you - same-day available
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  4
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Job Complete</h3>
                <p className="text-gray-600 text-sm">
                  Professional work completed to your satisfaction
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions - {service.name} in {city.name}
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  How much does {service.name.toLowerCase()} cost in {city.name}?
                </h3>
                <p className="text-gray-600">
                  {service.name} rates in {city.name} vary based on the job complexity and duration. Our transparent pricing starts from competitive hourly rates with no hidden fees. Get an instant quote when you book online.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Are your {service.name.toLowerCase()} professionals in {city.name} insured?
                </h3>
                <p className="text-gray-600">
                  Yes! All TaskBudy professionals providing {service.name.toLowerCase()} services in {city.name} are fully insured, background-checked, and verified. Your safety and peace of mind are our top priorities.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Can I get same-day {service.name.toLowerCase()} service in {city.name}?
                </h3>
                <p className="text-gray-600">
                  Yes! We offer same-day and emergency {service.name.toLowerCase()} services in {city.name} subject to availability. Book online or contact us directly for urgent requests.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  What areas of {city.name} do you cover?
                </h3>
                <p className="text-gray-600">
                  We provide {service.name.toLowerCase()} services throughout {city.name} and surrounding areas. Enter your postcode when booking to confirm coverage and get matched with nearby professionals.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Other Services in City */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Other Services in {city.name}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {otherServices.map((otherService) => (
                <Link
                  key={otherService.slug}
                  to={`/${otherService.slug}-${citySlug}`}
                  className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${otherService.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {otherService.name} in {city.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{otherService.description}</p>
                  <span className="text-teal-600 font-medium text-sm">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby Cities */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              {service.name} Services in Nearby Cities
            </h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {nearbyCities.map((nearbyCity) => (
                <Link
                  key={nearbyCity.slug}
                  to={`/${serviceSlug}-${nearbyCity.slug}`}
                  className="bg-white rounded-lg p-4 text-center hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <i className="ri-map-pin-fill text-2xl text-teal-600 mb-2"></i>
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {service.name} in {nearbyCity.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-teal-600 to-teal-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Book {service.name} in {city.name}?
            </h2>
            <p className="text-xl text-teal-50 mb-8">
              Get matched with verified professionals in minutes. Same-day service available.
            </p>
            <button
              onClick={handleBookNow}
              disabled={loading}
              className="px-8 py-4 bg-white text-teal-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Book Your Service Now'}
            </button>
          </div>
        </section>
      </nav>
    </>
  );
};

export default ServiceCityPage;
